package br.com.vvrefeicoes.portal.feature.rh

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppBadge
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.components.SearchableSelect
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.RhDepartmentUnit
import br.com.vvrefeicoes.portal.domain.model.RhUnitMatchStatus
import br.com.vvrefeicoes.portal.domain.model.RhUnitOption
import com.composables.icons.lucide.Building
import com.composables.icons.lucide.Lucide
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.rh_action_cancel
import portalvv.shared.generated.resources.rh_unit_review_all_set
import portalvv.shared.generated.resources.rh_unit_review_confirm
import portalvv.shared.generated.resources.rh_unit_review_director
import portalvv.shared.generated.resources.rh_unit_review_employees
import portalvv.shared.generated.resources.rh_unit_review_manager
import portalvv.shared.generated.resources.rh_unit_review_message
import portalvv.shared.generated.resources.rh_unit_review_no_units
import portalvv.shared.generated.resources.rh_unit_review_none
import portalvv.shared.generated.resources.rh_unit_review_pending
import portalvv.shared.generated.resources.rh_unit_review_search
import portalvv.shared.generated.resources.rh_unit_review_status_confirmed
import portalvv.shared.generated.resources.rh_unit_review_status_exact
import portalvv.shared.generated.resources.rh_unit_review_status_manual
import portalvv.shared.generated.resources.rh_unit_review_status_missing
import portalvv.shared.generated.resources.rh_unit_review_status_suggested
import portalvv.shared.generated.resources.rh_unit_review_title

/**
 * Conferência de unidades da importação: cada departamento da planilha vem com a unidade sugerida
 * pelo backend e o usuário confirma ou troca antes de gravar.
 */
@Composable
internal fun RhUnitReviewDialog(
    review: RhUnitReview,
    saving: Boolean,
    onSelect: (department: String, unitId: String?) -> Unit,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
) {
    val noneLabel = stringResource(Res.string.rh_unit_review_none)
    val labels = remember(review.units) { review.units.labels() }
    val options = remember(labels, noneLabel) { listOf(noneLabel) + labels.values }
    val unitsById = remember(review.units) { review.units.associateBy { it.id } }
    val pending = review.departments.count { it.unitId == null }

    AppModalCard(title = stringResource(Res.string.rh_unit_review_title), onDismiss = onDismiss) {
        Text(
            text = stringResource(Res.string.rh_unit_review_message),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        if (review.units.isEmpty()) {
            Text(
                text = stringResource(Res.string.rh_unit_review_no_units),
                style = MaterialTheme.typography.bodyMedium,
                color = AppPalette.Error900,
            )
        }
        Text(
            text =
                if (pending > 0) {
                    stringResource(Res.string.rh_unit_review_pending, pending, review.departments.size)
                } else {
                    stringResource(Res.string.rh_unit_review_all_set, review.departments.size)
                },
            style = MaterialTheme.typography.labelLarge,
            color = if (pending > 0) AppPalette.Warning900 else AppPalette.Success900,
        )
        // A planilha pode ter centenas de departamentos: a lista rola dentro do diálogo e só compõe
        // as linhas visíveis.
        LazyColumn(modifier = Modifier.fillMaxWidth().heightIn(max = 420.dp)) {
            items(review.departments, key = { it.department }) { row ->
                RhUnitReviewRow(
                    row = row,
                    unit = row.unitId?.let(unitsById::get),
                    selectedLabel = row.unitId?.let(labels::get) ?: noneLabel,
                    options = options,
                    enabled = !saving,
                    onSelect = { label -> onSelect(row.department, labels.entries.firstOrNull { it.value == label }?.key) },
                )
            }
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
        ) {
            AppButton(
                text = stringResource(Res.string.rh_action_cancel),
                onClick = onDismiss,
                variant = PortalButtonVariant.Outlined,
                enabled = !saving,
            )
            AppButton(
                text = stringResource(Res.string.rh_unit_review_confirm),
                onClick = onConfirm,
                enabled = !saving,
            )
        }
    }
}

@Composable
private fun RhUnitReviewRow(
    row: RhDepartmentUnit,
    unit: RhUnitOption?,
    selectedLabel: String,
    options: List<String>,
    enabled: Boolean,
    onSelect: (String) -> Unit,
) {
    Column(
        modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space8),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space4),
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
        ) {
            Text(
                text = row.department,
                style = MaterialTheme.typography.titleSmall,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f),
            )
            val badge = row.badge()
            AppBadge(text = stringResource(badge.label), containerColor = badge.container, contentColor = badge.content)
        }
        Text(
            text = stringResource(Res.string.rh_unit_review_employees, row.employees),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        SearchableSelect(
            leadingIcon = Lucide.Building,
            options = options,
            selected = selectedLabel,
            onSelect = onSelect,
            modifier = Modifier.fillMaxWidth(),
            searchPlaceholder = stringResource(Res.string.rh_unit_review_search),
            searchable = true,
            underlined = true,
            enabled = enabled,
        )
        val details =
            listOfNotNull(
                unit?.director?.let { stringResource(Res.string.rh_unit_review_director, it) },
                unit?.manager?.let { stringResource(Res.string.rh_unit_review_manager, it) },
            )
        if (details.isNotEmpty()) {
            Text(
                text = details.joinToString(" · "),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
}

private class RhUnitBadge(
    val label: StringResource,
    val container: Color,
    val content: Color,
)

/** O selo acompanha a escolha atual: sem unidade ele alerta, mesmo que houvesse uma sugestão. */
private fun RhDepartmentUnit.badge(): RhUnitBadge =
    when {
        unitId == null -> RhUnitBadge(Res.string.rh_unit_review_status_missing, AppPalette.Error100, AppPalette.Error900)
        status == RhUnitMatchStatus.SUGGESTED -> RhUnitBadge(Res.string.rh_unit_review_status_suggested, AppPalette.Warning100, AppPalette.Warning900)
        status == RhUnitMatchStatus.CONFIRMED -> RhUnitBadge(Res.string.rh_unit_review_status_confirmed, AppPalette.Success100, AppPalette.Success900)
        status == RhUnitMatchStatus.EXACT -> RhUnitBadge(Res.string.rh_unit_review_status_exact, AppPalette.Success100, AppPalette.Success900)
        else -> RhUnitBadge(Res.string.rh_unit_review_status_manual, AppPalette.Neutral200, AppPalette.Neutral900)
    }

/** Rótulo de cada unidade no seletor (id -> rótulo); nomes repetidos ganham a diretoria para diferenciar. */
private fun List<RhUnitOption>.labels(): Map<String, String> {
    val repeated = groupingBy { it.name }.eachCount().filterValues { it > 1 }.keys
    return associate { unit ->
        unit.id to if (unit.name in repeated && unit.director != null) "${unit.name} · ${unit.director}" else unit.name
    }
}
